'use strict'

// ublock (and possibly other blockers) blocks files named "tracker.js". So just rename it (short for Data Collector) and voila.

// enum to specify which region of the widget the event comes from
const Area = {
  MAIN: 'main',
  MAP: 'map',
  CLIENT_DOM: 'clientdom',
}
// module enum
const moduleType = {
  WTB: 1,
  BT: 3,
  VT: 4
}

class Tracker {
  constructor(params, wtbConf, timeOffset){
    this._wtbConf = wtbConf
    this.logHost = Utils.changeAWSEndpoint('https://trace.swaven.com/log', this._wtbConf.region)
    this.moduleName = (Object.entries(moduleType).find(kv => kv[1] === this._wtbConf.module) || ['WTB'])[0].toLowerCase(),
    this.trkHost = Utils.setTrackingHost(this.moduleName.toUpperCase(), this._wtbConf.region)

    this._urlRegExp = new RegExp('^(https?:)?\/\/')
    this._country = wtbConf.countries.length === 1 ? wtbConf.countries[0] : null
    this.customTrackingOldNaming = params.customTrackingOldNaming
    this.fmt = app.isMedia ? `${params.width}x${params.height}` : 'awe'
    this._offset = timeOffset
    this.session = params.session
    this.template = params.templatePath
    this.rfr = params.referrer
    this.rfr2 = params.referrer2.substring(0, 2048) // truncate rfr2 only for tracking, full url is needed in app.
    this.trackingPixels = params.trackingPixels
    this.hasPrices = +!!(params.hasPrices && app.displayPrices)
    this.test = +!!(app.test || params.renderContext)
    this.openerIdx = null // idx of the button that opened the widget
    this.availReqId = null
    this.hasDroppedLiveRampPixel = false;
    this.dntDefault = params.dntDefault;
    this.contextLoad = params.contextLoad // true if widget is loaded through host context monitoring (implementation method v2)

    // single queue for both standard and custom trackings.
    // Array of {action:string, payload: standard tracking object}
    this._dntQueue = []

    this._customTrkVars = null
    if (params.customTrkVars){
      this._customTrkVars = {}
      Object.entries(params.customTrkVars).forEach(([k,v])=> {
        this._customTrkVars['cust_' + k] = v
      })
    }

    this.build = params.build
    this.buildName = window.location.pathname.match(/awe\/\d+.\w+\/(\w+)/)?.[1] || ''
    this.pageId = params.pageId

    // backoffice callback actions uses 'deploy' instead of 'dploy'.
    if (wtbConf.callbackactions && wtbConf.callbackactions.includes('deploy'))
      wtbConf.callbackactions[wtbConf.callbackactions.indexOf('deploy')] = 'dploy'

    if (app.withSafeFrame)
      this.safeFrameDetector = 'framework'

    // stores trackers' state
    this.consentMap = this._buildConsentMap(wtbConf.trkcfg)

    Tracker.dropTag('pv', {auto: true})

    // safeframe info may be obtained only at at a later time, so listen to message
    // for all media widgets.
    if (app.isMedia){
      // remove clicktag update handler after some time.
      const tmId = setTimeout(() => { hostCom.off('awe:clicktag', fn) }, 2e4)

      // clicktag update handler
      const fn = (e) => {
        this.clicktag = e.clicktag
        Tracker.log('clicktagUpdate') // tracking to know the time taken for tracker update
        if (e.stop){ // sender says no need to wait anymore, unbind & clear removal timeout
          hostCom.off('awe:clicktag', fn)
          clearTimeout(tmId)
        }
      }
      hostCom.on('awe:clicktag', fn)
    }

    hostCom.on('awe:tracking', (e) => {
      if (!e.params || !e.params.event)
        return

      if (!e.params.area)
        e.params.area = Area.CLIENT_DOM // tracking events coming from hostCom are postMessages from client page.

      const action = e.params.event
      delete e.params.event

      Tracker.dropTag(action, e.params)
    })

    hostCom.on('awe:log', (e) => {
      if (!e.params)
        e.params = {}
      if (!e.params.area)
        e.params.area = Area.CLIENT_DOM // tracking events coming from hostCom are postMessages from client page.

      Tracker.log(e.params.event, e.params)
    })

    // handle DoNoTrack consent given/rejected
    hostCom.on('awe:dnt', (e) => {
      if (app.dnt !== e.params.enabled){
        app.dnt = e.params.enabled

        // tracking allowed: flush queued trackings
        if (!app.dnt)
          this._flushDNT(e.params.session)

        if (!app.dnt
          && this._wtbConf.operator?.isLiveRampEnabled
          && !this.hasDroppedLiveRampPixel) {
          this._dropLiveRampPixel();
        }
      }
    })

    // message from cookieConsent, a tracker has been accepted or denied
    hostCom.on('awe:consentChange', e => {this.consentChange(e.params)})

    Tracker.ready = true
    Tracker.ref = this
    this.emptyQueue()

    //  Drop LiveRamp pixel on load only if
    //    DNT was on by default, but was disabled due to consent
    //    before the widget was initialized.
    //  Otherwise, delegate this to the DNT event handler.
    //  We don't want to drop the pixel on load if
    //    DNT was NOT on by default, because that's the default state
    //    for experiences, and we don't want to drop the pixel
    //    unless it was explicitly set up.
    if (this.dntDefault && !app.dnt
      && this._wtbConf.operator?.isLiveRampEnabled
      && !this.hasDroppedLiveRampPixel) {
      this._dropLiveRampPixel();
    }

    if (window.ReportingObserver)
      this._setObserver()
  }

  // Starts a report observer and adds new properties to it.
  _setObserver(){
    let obs = new ReportingObserver((reports, observer) => {
      reports.forEach(r => {
        console.debug(`Triggered a ${r.type} report`)

        // Report object does not support Object.keys, so use iterator on object instead
        const data = {reportType: r.type, cpus: navigator.hardwareConcurrency, memory: navigator.deviceMemory}
        for (let key in r.body){
          if (typeof r.body[key] !== 'function' && r.body[key] != null)
            data[key] = r.body[key]
        }

        Tracker.log('report', data)
      })
    }, {types: ['deprecation', 'intervention', 'crash']})

    obs.observe()
  }

  // inserts pixel in document: loading the url performs the actual tracking
  _addPixel(url){
    const px = document.createElement('img')
    px.style.display = 'none'
    px.src = url
    px.setAttribute('role', 'none')
    px.setAttribute('alt', 'event-data')
    px.setAttribute('title', 'event-data')
    px.onload = function(e){e?.currentTarget?.remove() }
    document.body.appendChild(px)
  }

  _dropLiveRampPixel() {
    const liveRampPixelId = 712896;
    const encodedPData = encodeURIComponent(`wtbid=${app.wtbid}`);

    this._addPixel(`https://di.rlcdn.com/api/segment?pid=${liveRampPixelId}&pdata=${encodedPData}`);

    this.hasDroppedLiveRampPixel = true;
  }

  // performs tracking.
  // - data acquisition/formatting for standard and custom tracking
  // - queues event if DNT enabled OR performs tracking immediately (standard and custom).
  _dropTag(action, params){

    params.ts = performance.now()

    const payload = this._getTrackingParam(action, params) //get parameters object
    let customTrkData

    if (!params.log){ // tech log is not for custom tracking
      customTrkData = this._getCustomTrkData(action, params)
      this._customTracking(action, customTrkData)
      this._sendEventsToGTM(action, params, customTrkData)
    }

    if (app.dnt){
      // enqueue a single event that contains both default tracking data,
      // and custom tracking object if relevant
      this._dntQueue.push({action, payload})
    }
    else{
      this._immediateDropTag(action, payload)
    }
  }

  // adds some data to payload, serializes it and handles tracking logic
  _immediateDropTag(action, payload){
    const {s_url, ...qsParams} = payload

    let queryString = Object.entries(qsParams)
            .filter(([k, v]) => v != null && v !== '') // remove empty/null/undefined values
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&') // and convert it to a query string

    if (s_url)
      queryString += `&s_url=${encodeURIComponent(s_url)}`

    queryString += `&rfr2=${encodeURIComponent(this.rfr2)}`

    if (this.rfr)
      queryString += `&rfr=${encodeURIComponent(this.rfr)}`

    const useBeacon = action === 'buy' && navigator.sendBeacon

    if (useBeacon)
      navigator.sendBeacon(`${this.logHost}/${action}?${queryString}&beacon=1`)
    else
      this._addPixel(`${this.logHost}/${action}?${queryString}`)

    if (!payload.log){ // default cloudFront tracking (not technical log)
      if (useBeacon) // buy event is duplicated by beacon & pixel to have data redundancy
        navigator.sendBeacon(`${this.trkHost}/post?action=${action}&${queryString}&beacon=1`)

      // pixel tracking for all actions. 'buy' renamed to 'buypx'
      let pxAction = action.replace('buy', 'buypx')
      this._addPixel(`${this.trkHost}/px.png?action=${pxAction}&${queryString}`)
    }

    if (app.$refs.logger)
      app.$refs.logger.logTrk(action, qsParams)
  }

  // pushes events and associated data to dataLayer for brands to use in their GTM containers
  _sendEventsToGTM(action, params, customTrkData) {
    let payload = this._formatGTMPayload(action, params, customTrkData)

    // return early for events that do not map to a GTM event
    if (!payload.event) {
      return
    }
    hostCom.send('awe:sendEventToHostGTM', { payload })
  }

  _formatGTMPayload(action, params, customTrkData) {
    let payload = {
      wtbid: this._wtbConf.Id,
      geo_country: this._wtbConf.geoCountry,
      commerce_type: 'brand.com',
      experience_name: this._wtbConf.sourceName || this._wtbConf.name,
      account_name: this._wtbConf.operator.account?.name || '',
      subaccount_name: this._wtbConf.operator.name || '',
    }
    try {
      if (action === 'clk' && params.auto) {
        payload.event = 'mikmak_load'
      } else if (action === 'buy') {
        payload.event = 'mikmak_checkout'
      } else if (action === 'clk' && !params.auto) {
        payload.event = 'mikmak_product_selected'
      } else if (action === 'close') {
        payload.event = 'mikmak_close'
      } else if (action === 'clktab') {
        payload.event = 'mikmak_tab'
      } else if (action === 'geoloc') {
        payload.event = 'mikmak_update_location'
      } else if (action === 'availabilities-update') {
        payload.event = 'mikmak_retailer_list_displayed'
      } else if (action === 'clkloc'){
          if (params.clkloc_type === 'tel')
            payload.event = 'mikmak_call_offline_store'
          else if (params.clkloc_type === 'googlemaps' || !params.clkloc_type) {
            // googlemaps or generic offline store click
            payload.event = 'mikmak_click_offline_store'
          }
      }

      if (customTrkData.location) {
        payload.location_zipcode = customTrkData.location.zipCode
        payload.location_city = customTrkData.location.city
        payload.location_state = customTrkData.location.region
        payload.location_country = customTrkData.location.country
      }

      // add product info
      if (customTrkData.product) {
        payload.product_gtin = customTrkData.product.ean
        payload.product_name = customTrkData.product.name
        payload.product_brand = customTrkData.product.brand
        payload.product_id = customTrkData.product.sourcePid
        payload.product_package = customTrkData.product.pkg
        payload.product_model = customTrkData.product.model
        payload.product_line = customTrkData.product.productLine
        payload.product_range = customTrkData.product.range
        payload.product_category = customTrkData.product.categoryName

        // only set product_variant if it exists
        if (customTrkData.product.name2) {
          payload.product_variant = customTrkData.product.name2
        }
      }

      // add retailer list displayed specific payload structure
      if (action === 'availabilities-update') {
        // Add tab_displayed field
        payload.tab_displayed = params.tab_displayed || ""

        // build retailer list from availabilities
        if (params.avails) {
          // Full list including duplicates (reflects what's shown to user)
          payload.retailer_list = params.avails.map(avail => {
            const retailerItem = {
              retailer_id: avail.retailer.id,
              store_id: avail.store.id,
              retailer_name: avail.retailer.id === 3364 ? "VIP" : avail.retailer.name,
              store_name: avail.store.name,
              channel_type: avail.store.online ? "online" : "offline",
            }
            
            // Only include in_stock if includeOutOfStock is true
            if (params.includeOutOfStock) {
              retailerItem.in_stock = avail.product.available
            }
            
            return retailerItem
          })
          
          // Deduplicated list
          const uniqueRetailers = new Map()
          params.avails.forEach(avail => {
            if (!uniqueRetailers.has(avail.retailer.id)) {
              const retailerItem = {
                retailer_id: avail.retailer.id,
                store_id: avail.store.id,
                retailer_name: avail.retailer.id === 3364 ? "VIP" : avail.retailer.name,
                store_name: avail.store.name,
                channel_type: avail.store.online ? "online" : "offline",
              }
              
              // Only include in_stock if includeOutOfStock is true
              if (params.includeOutOfStock) {
                retailerItem.in_stock = avail.product.available
              }
              
              uniqueRetailers.set(avail.retailer.id, retailerItem)
            }
          })
          
          payload.retailer_list_unique = Array.from(uniqueRetailers.values())
        }
      }
    
      // add purchase or click specific info
      if (action === 'buy' || action === 'clkloc') {
        payload.product_price = customTrkData.product.price
        payload.product_price_currency = customTrkData.product.currency

        payload.product_price_text = (new Intl.NumberFormat(app.locale, {
          style: 'currency',
          currency: customTrkData.product.currency,
          minimumFractionDigits: 2,
          })).format(customTrkData.product.price)
        payload.retailer_id = customTrkData.retailer.id
        payload.retailer_name = customTrkData.retailer.name
        payload.retailer_name_sanitized = Utils.sanitizeString(customTrkData.retailer.name)
        payload.store_id = customTrkData.store.id
        payload.store_name = customTrkData.store.name
        payload.store_city = customTrkData.store.location.city
        payload.store_zipcode = customTrkData.store.location.zipCode
      }

      if (action === 'clktab') {
        payload.tab_clicked = customTrkData.tab
      }
    } catch (e) {
      console.error('Error formatting GTM payload', e)
    }

    return payload
  }

  static dropTag(type, params = {}){
    if (Tracker.ready)
      Tracker.ref._dropTag(type, params)
    else
      Tracker.enqueue(type, params)
  }

  // drops a pixel tag for technical logging purposes
  static log(type, params = {}){
    params.log = 1

    if (Tracker.ready)
      Tracker.ref._dropTag(type, params)
    else
      Tracker.enqueue(type, params)
  }

  // static method that populates a static set when tracker is not instanciated.
  static enqueue(type, params){
    if (!Tracker._queue)
      Tracker._queue = new Set()
    Tracker._queue.add({action: type, params: params})
  }

  // drops clicktag pixel
  dropClicktag(trigger){
    // in safeFrame context, clicktag is retrieved by loader and sent to widget.
    // Otherwise we ask the loader to do it.
    if (app.withSafeFrame){
      this._addPixel(this.clicktag)
      this._dropTag('clicktag', {url: this.clicktag, trigger: trigger})
    }
    else
      hostCom.send('awe:clicktag', {trigger: trigger})
  }

  _getTrackingParam(action, data = {}){
    var trkData = app.getTrkData()

    // common parameters
    let params = {
      wtbid: this._wtbConf.Id,
      fmt: this.fmt,
      module: this.moduleName,
      type: 'awe',
      v: Date.now(),
      lang: app.locale,
      opscode: this._wtbConf.opscode || '',
      area: data.area || Area.MAIN,
      pageid: this.pageId,
      iid: window.name,
      reqpid: app.eans,
      ct: this._country,
      ct_context: app.country,
      geocountry: this._wtbConf.geoCountry,
      touchpoint: this._wtbConf.touchPoint,
      dpr: this.hasPrices,
      test: this.test,
      template: this.template,
      trkOrigin: 'daco',
      tzOffset: (new Date()).getTimezoneOffset(),
      vue: Vue.version,
    }

    if (app.isMedia && app.layoutName)
      params.layout = app.layoutName

    if (app.masterProductMode && app.productGroups && app.product )
      params.pids = app.productGroups.get(app.product).map(x => x.ean)

    if (action !== 'pv' && action !== 'dploy' && (data.avail?.product || app.product))
      params.pid = data.avail?.product?.ean || app.product.ean

    if (trkData.avgpc != null)
      params.avgpc = trkData.avgpc
    if (trkData.avgdst != null)
      params.avgdist = trkData.avgdst
    if (trkData.nbres != null)
      params.nbres = trkData.nbres
    if (trkData.nbrefret != null)
      params.nbrefret = trkData.nbrefret
    if (app.appId != null)
      params.appid = app.appId
    if (this.availReqId)
      params.avreqid = this.availReqId

    if (this.session){
      params = this._setSession(params)
    }

    if (this.openerIdx != null)
      params.opnIdx = this.openerIdx

    if (this._customTrkVars)
      params = Object.assign(params, this._customTrkVars)



    if (data.outbound != null){
      params.outbound = +!!data.outbound
       // remove property from object, to prevent it from being overwritten by defaut statement
       // in the switch below when action is not explicitely mentionned (e.g. a fallback)
      delete data.outbound
    }

    if (app.error === 'UNAVAILABLE_COUNTRY_ERROR')
      params.oos = 1

    if (data.avail){
      const availParams = this._getAvailParams(data.avail)
      Object.entries(availParams).forEach(([k, v]) => params[k] = v)
    }

    // Add geo regional information
    const location = app.getLocation()

    // add location info only when session exists
    if (!location.isEmpty && this.session){
      params.uloc_country = location.country
      params.uloc_region = location.region
      params.uloc_city = location.city
      params.uloc_latitude = Math.round(location.latitude * 100) / 100
      params.uloc_longitude = Math.round(location.longitude * 100) / 100
      params.uloc_source = location.source
      params.uloc_type = location.type
      params.uloc_auto = +location.auto
      params.uloc_zip_code = location.zipCode

      if (location.isDefault)
        params.uloc_isDefault = 1
    }

    switch (action){
      case 'pv':
        params.auto = 1
        break
      case 'dploy':
        params.auto = +!!data.auto
        if (data.opnidx)
          params.opnidx = data.opnidx
        if (data.src)
          params.src = data.src
        break
      case 'buy':
        if (data.avail){
          if (data.avail.clkUrlAffiliate)
            params.clkurlaff_prgid = data.avail.retailer.affiliatePrgId
          params.clkurlaff = +!!data.avail.clkUrlAffiliate
          params.clkurlt = data.avail.clkUrlType
        }
        if (data.leadid)
          params.leadid = data.leadid
        params.auto = +!!data.auto
        break
      case 'geoloc':
        params.auto = 0 // geoloc event is always triggered by a user action
        break
      case 'clkloc':
        params.clkloc_type = data.clkloc_type || 'map'

        if(params.clkloc_type === 'retailerHome') {
          params.rid = data.avail.retailer.id
          params.rurl = data.avail.retailer.url
        }
      case 'clkRoute':
      case 'appointment':
        params.auto = +!!data.auto
        break
      case 'showmap':
        params.auto = +!!data.auto
        break
      case 'clk':
        params.auto = +!!data.auto
        params.autoSel = +!!data.autoSel
        break
      case 'apires':
        params.sids = app.getStoreIds()
        params.auto = 1
        break
      case 'close':
        params.auto = +!!data.auto
        break
      case 'unavlbsids':
        params.auto = 1
        params.sids = data.sids
        break
      case 'storedisplayed':
        params.auto = +!!data.auto
        params.sids = data.sids
        break
      case 'availabilities-update':
        params.auto = +!!data.auto
        params.rids = data.avails ? data.avails.map(avail => avail.retailer.id) : []
        if (data.tab_displayed !== undefined) {
          params.tab_displayed = data.tab_displayed
        }
        break
      case 'clkout':
      case 'custredirect':
        params.auto = +!!data.auto
        break
      case 'clicktag':
        params.url = data.url
        params.swavenDomain = +(/\/\/[^\/]+?\bswaven\b/i.test(params.url))
        params.trigger = data.trigger
        break
      case 'btn':
      case 'btn_unavailable':
        params.reqpid = data.reqpid
        params.idx = data.idx
        break
      default:
        params = Object.assign(params, data)
        break
    }

    params.build = this.build
    params.buildName = this.buildName
    params.setupVersion = this.contextLoad ? 2 : 1,
    params.safeframe = +app.withSafeFrame
    if (app.withSafeFrame)
      params.safeframeDetector = this.safeFrameDetector

    params.ts = Math.floor(this._offset + data.ts)

    if (app.renderContext)
      params.renderctx = app.renderContext

    if (app.draft)
      params.draft = 1

    return params
  }

  // Add session data to params object.
  // Done at tracking time and on DNT flushing.
  _setSession(params){
    if (this.session.ts)
      params.s_ts = this.session.ts
    if (this.session.url)
      params.s_url = this.session.url
    if (this.session.referrer)
      params.s_referrer = this.session.referrer
    if (this.session.id)
      params.s_id = this.session.id

    return params
  }

  // returns tracking data for given availability/store
  _getAvailParams(avail){
    return {
      prc: avail.price.amount,
      dist: avail.store.distance,
      dlv: this._getDlvParam(avail.store),
      sid: avail.store.id,
      sids: app.getStoreIds(), // Adding all store ids whenever we have a store to have a global context in tracking
      avpid: avail.product.ean,
      retpid: avail.product.retailerPdtId,
    }
  }

  // returns type of delivery for the store
  _getDlvParam(store){
    if (store.drive || store.collect)
      return 'driveOrCollect'
    else if (store.brick)
      return 'brick'
    else if (store.homeDelivery)
      return 'HD'
    else
      return ''
  }

  // perform client-defined tracking
  _customTracking(type, trkData){
    // update custom tracking data to use old naming scheme if necessary
    let customTrkData = this.customTrackingOldNaming ? this._getCustomTrkDataOldNaming(trkData) : trkData;

    // client tracking allowed only if callbacks are enabled and the action is whitelisted (no array or empty array means all actions allowed).
    if (this._wtbConf.callbackenabled && (!this._wtbConf.callbackactions || this._wtbConf.callbackactions.length === 0 || (this._wtbConf.callbackactions.includes(type)))){
      if (this.trackingPixels)
        this._dropClientPixels(customTrkData) // client-defined pixel trackers

      hostCom.send('awe:trackingCallback', { trkData: customTrkData }) // send data to loader, to pass to client-defined tracking callback function
    }

    // server-side custom tracking (using tracking plan)
    if (this._wtbConf.trkcfg){
      let actions = this._wtbConf.trkcfg.actions.filter(x => x.Code === type && (!x.retid || (x.retid === customTrkData.retailer?.id)))
      if (actions.length > 0)
        this._serverTracking(actions, customTrkData)
    }
  }

  _getCustomTrkData(action, data = {}){
    const location = app.getLocation()

    let trkData = {
      action: action,
      ts: Date.now(),
      wtbid: this._wtbConf.Id,
      fmt: this.fmt,
      type: 'awe',
      ids: app.eans,
      auto: !!data.auto,
      geoCountry: this._wtbConf.geoCountry,
      location: {
        address: location.address,
        city: location.city,
        zipCode: location.zipCode,
        region: location.region,
        country: location.country,
        latitude: location.latitude,
        longitude: location.longitude,
      }
    }

    let product = app.product // tracked product
    // avail-level event in multi-offer: the product may not be the currently selected one.
    if (app.multiOffer && data.avail){
      // in master product mode, the products list is filtered to contain only the master ones,
      // variants are mapped in productGroups.
      if (app.masterProductMode){
        const itor = app.productGroups.values()
        let pdt, line
        while (line = itor.next().value){
          pdt = line.find(x => x.ean === data.avail.product.ean)
          if (pdt)
            break
        }
        product = pdt
      }
      else {
        // find in products
        product = app.products.find(x => x.ean === data.avail.product.ean)
      }
    }

    // only when the widget is opened by a button
    if (this.openerIdx != null) {
      trkData.sticky = !!(this.openerIdx == 0) // true if openerIdx is 0 or "0"
    }

    if (app.isMedia && app.layoutName)
      trkData.layout = app.layoutName

    const extData = app.getTrkData()
    if (extData.nbres)
      trkData.resultCount = extData.nbres

    if (product && Object.keys(product).length > 0){
      trkData.product = {
        id: product.id,
        ean: product.ean,
        name: Utils.removeHTML(product.name),
        name2: Utils.removeHTML(product.name2),
        pkg: Utils.removeHTML(product.pkg),
        brand: product.brand,
        model: product.model,
        categoryName: product.catName,
        sourcePid: product.sourcePid,
        range: product.range,
        productLine: product.productLine,
      }
    }

    // for some actions, include store & retailer info
    if (['buy','clkloc','clkRoute', 'appointment'].indexOf(action) > -1 && data.avail != null){
      trkData.store = {
        id: data.avail.store.id,
        name: data.avail.store.name,
        dlv: this._getDlvParam(data.avail.store),
        url: data.avail.store.url,

        location: {
          city: data.avail.store.location.city,
          zipCode: data.avail.store.location.zipCode,
          country: data.avail.store.location.country
        }
      }

      trkData.retailer = {
        id: data.avail.retailer.id,
        name: data.avail.retailer.name
      }

      if (trkData.product){
        trkData.product.retailerPdtId = data.avail.product.retailerPdtId
        trkData.product.price = data.avail.price.amount
        trkData.product.currency = data.avail.price.currency
      }
    }

    if (action === 'clktab') {
      trkData.tab = data.tab
    }

    if (action === 'clkloc') {
      trkData.clklocType = data.clkloc_type
    }

    // Hasbro custom event data
    if (action === 'age-gate-redirect-cancel') {
      trkData.cancelSrc = data.cancelSrc;
    }

    if (action === 'age-gate-redirect-continue') {
      trkData.continueSrc = data.continueSrc;
    }

    return trkData
  }

  _getCustomTrkDataOldNaming(trkData){
    // make a copy of the object to avoid modifying the original
    let trkDataCustom = Object.assign({}, trkData)

    // If widget requires old name scheme, transform object to use old property names
    trkDataCustom.compat = true
    if (trkDataCustom.resultCount)
      trkDataCustom.nbres = trkDataCustom.resultCount

    if (trkDataCustom.product){
      trkDataCustom.prd = {
        pid: trkDataCustom.product.id,
        ean: trkDataCustom.product.ean,
        pname: trkDataCustom.product.name,
        pname2: trkDataCustom.product.name2,
        ppckg: trkDataCustom.product.pkg,
        pbr: trkDataCustom.product.brand,
      }
    }
    if (trkDataCustom.store && trkDataCustom.product){
      trkDataCustom.store = {
        pr: trkDataCustom.product.price,
        scity: trkDataCustom.store.location.city,
        sstate: trkDataCustom.store.location.state,
        szc: trkDataCustom.store.location.zipCode,
        country: trkDataCustom.store.location.country,
        rid: trkDataCustom.retailer.id,
        rname: trkDataCustom.retailer.name,
        rpid: trkDataCustom.product.retailerPdtId,
        sid: trkDataCustom.store.id,
        sname: trkDataCustom.store.name,
        dlv: trkDataCustom.store.dlv,
        surl: trkDataCustom.store.url
      }
    }
    trkDataCustom.loc = {zipcode: trkDataCustom.location.zipCode}

    delete trkDataCustom.resultCount
    delete trkDataCustom.product
    delete trkDataCustom.retailer
    delete trkDataCustom.location

    return trkDataCustom
  }

  // edits client-provided tracking urls to replace placeholders with actual values, and add them as pixel tags
  _dropClientPixels(data){
    var urls = this.trackingPixels[data.action]
    if (urls){
      if (!Array.isArray(urls))
        urls = [urls]
      urls.forEach(url => {
        if (this._urlRegExp.test(url)){
          url = this._replacePlaceholders(url, data, true)

          if (app.withSafeFrame)
            this._addPixel(url)
          else
            hostCom.send('awe:dropPixel', {url: url})
        }
      })
    }
  }

  // server-defined custom tracking: action can be a pixel tag url to insert or a script to add
  _serverTracking(actions, data){
    actions.forEach(action => {
      if (typeof action.value !== 'string'){
        console.log('Server custom tracking action defined but no value, please verify configuration in database')
        return
      }

      let allowed = true,
          trackerState

      // check whether tracker is allowed
      if (action.tracker && this.consentMap && this.consentMap.has(action.tracker)){
        trackerState = this.consentMap.get(action.tracker)
        allowed = trackerState.accepted
      }

      let value, type

      // check tracking type, url or script.
      if (this._urlRegExp.test(action.value)){
        type = 'url'
        value = this._replacePlaceholders(action.value, data, true)
      }
      else{
        type = 'script'
        value = this._replacePlaceholders(action.value, data, false)
      }

      // drops or queues tracking
      if (allowed)
        this._dropServerTracking({[type]: value})
      else
        trackerState.queue.push({[type]: value})

    })
  }

  // sends to loader for host-side tracking. If safeframe, drops pixel directly.
  _dropServerTracking(data){
    if (data.url){
      if (app.withSafeFrame)
        this._addPixel(data.url)
      else
        hostCom.send('awe:dropPixel', data)
    }
    else if (data.script){
      hostCom.send('awe:addScript', data)
    }
  }

  // process string (url or script) to replace placeholders (delimited by double-brackets) with the actual corresponding value
  _replacePlaceholders(str, data, encode){
    if (data == null) // no data => nothing to replace
      return str

    // old name scheme: Each property in data has a unique name, placeholder is a single value (e.g. '{{sid}}').
    // we loop recursively over all keys, and replace the corresponding placeholder with the associated value.
    if (this.customTrackingOldNaming){
      Object.entries(data).forEach(([key, val]) => {
        if (typeof val === 'object' && !Array.isArray(val))
          str = this._replacePlaceholders(str, val, encode)
        else{
          let regexp = new RegExp(`{{${key}}}`, 'g')
          str = str.replace(regexp, encode ? encodeURIComponent(val) : val)
        }
      })
    }
    else{
      // new name scheme: properties are not unique. placeholder is a path to the value (e.g. '{{store.location.country}}').
      // We decompose each placeholder into a list of keys and mine the data object key after key until we've processed to whole path.
      str = str.replace(/{{(.+?)}}/g, (match, pattern) => {
        const keys = pattern.split('.')
        let value = data

        try{
          for (let i = 0; i < keys.length; i++){
            value = value[keys[i]]
          }

          if (value == null)
            return ''
          else
            return encode ? encodeURIComponent(value.toString()) : value.toString()
        }
        catch(ex){ // on error (e.g. accessing a null object), return the placeholder
          return match
        }
      })
    }

    return str
  }

  // instance method, process all messages in queue once tracker is instanciated.
  emptyQueue(){
    if (!Tracker._queue) return

    for (let elem of Tracker._queue){
      Tracker._queue.delete(elem)
      this._dropTag(elem.action, elem.params)
    }

    delete Tracker._queue
  }

  // when disabling DNT, flush queued trackings
  _flushDNT(session){
    if (session)
      this.session = session

    // empty FIFO queue
    while (this._dntQueue.length > 0){
      let {action, payload} = this._dntQueue.shift()
      payload.flush = 1
      if (session)
        payload = this._setSession(payload)

      this._immediateDropTag(action, payload)
    }
  }

  // creates a map from tracking plan action: tracker name as key, status & queue as value.
  _buildConsentMap(trkPlan){
    if (trkPlan && trkPlan.actions){
      const consentMap = new Map()

      trkPlan.actions.forEach(x => {
        // that's only for actions that require consent and define a tracker
        if (!x.consent || !x.tracker) return

        if (!consentMap.has(x.tracker))
          consentMap.set(x.tracker, {
            accepted: false, // trackers are blocked by default, until tarteaucitron tells us it's ok
            queue: [] // store blocked trackings, to replay later if tracker is accepted.
          })
      })

      if (consentMap.size)
        return consentMap
      else
        return null
    }
  }

  consentChange({tracker, consent}){
    // get tracker state
    const data = this.consentMap && this.consentMap.get(tracker)

    if (!data){
      const msg = `No data for tracker ${tracker} in consentMap`
      console.error(msg)
      Tracker.log('error', {msg})
      return
    }

    // set new state & flush queue if needed
    data.accepted = !!consent

    if (data.accepted && data.queue.length){
      // console.debug('dequeue server trackings')
      for (let elem of data.queue){
        this._dropServerTracking(elem)
      }
      data.queue = []
    }
  }
}

Tracker.ready = false